import { Button, Camera, ImagePlus, Send, Textarea, X } from "@app/ui";
import { useEffect, useState } from "react";
import { useI18n } from "../../lib/useI18n.js";
import { toast } from "../../stores/toast.store.js";
import {
  captureDisplayScreenshot,
  FEEDBACK_EMAIL,
  pickFeedbackImage,
  sendFeedback,
  type FeedbackImage,
} from "./feedback.js";

interface FeedbackComposerProps {
  initialNote?: string | undefined;
  initialImage?: FeedbackImage | undefined;
  source?: string | undefined;
  onSent?: (() => void) | undefined;
}

export function FeedbackComposer({ initialNote = "", initialImage, source, onSent }: FeedbackComposerProps) {
  const { t } = useI18n();
  const [note, setNote] = useState(initialNote);
  const [image, setImage] = useState<FeedbackImage | undefined>(initialImage);
  const [busy, setBusy] = useState(false);
  const [captureBusy, setCaptureBusy] = useState(false);

  useEffect(() => {
    setNote(initialNote);
    setImage(initialImage);
  }, [initialNote, initialImage]);

  async function attachImage() {
    const picked = await pickFeedbackImage();
    if (picked) setImage(picked);
  }

  async function captureScreen() {
    setCaptureBusy(true);
    try {
      const captured = await captureDisplayScreenshot();
      if (captured) setImage(captured);
      else toast.error(t("feedback.captureUnavailable"));
    } catch (e) {
      if (isAbortError(e)) return;
      toast.error(t("feedback.captureFailed"));
    } finally {
      setCaptureBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    try {
      const result = await sendFeedback({ note, image, source });
      toast.success(result === "shared"
        ? t("feedback.readyToShare", { email: FEEDBACK_EMAIL })
        : result === "compose-tab"
          ? t("feedback.shareTabOpened")
        : image
          ? t("feedback.emailDraftOpened")
          : t("feedback.emailDraftOpened"));
      onSent?.();
    } catch (e) {
      if (isAbortError(e)) return;
      toast.error(t("feedback.openFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="mb-1 block text-sm font-medium" htmlFor="feedback-note">{t("feedback.notes")}</label>
        <Textarea
          id="feedback-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("feedback.placeholder")}
          className="min-h-28"
        />
      </div>

      {image ? (
        <div className="rounded-md border bg-background p-2">
          <div className="mb-2 flex items-center justify-between gap-2 text-sm">
            <span className="truncate text-muted-foreground">{image.name}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setImage(undefined)}>
              <X className="h-4 w-4" /> {t("feedback.removeImage")}
            </Button>
          </div>
          <img src={image.dataUrl} alt={t("feedback.previewAlt")} className="max-h-56 w-full rounded object-contain" />
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          {t("feedback.attachmentHint")}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={captureScreen} disabled={captureBusy || busy}>
          <Camera className="h-4 w-4" /> {captureBusy ? t("feedback.capturing") : t("feedback.captureScreen")}
        </Button>
        <Button type="button" variant="outline" onClick={attachImage} disabled={busy}>
          <ImagePlus className="h-4 w-4" /> {t("feedback.attachImage")}
        </Button>
        <Button type="button" onClick={submit} disabled={busy || (!note.trim() && !image)}>
          <Send className="h-4 w-4" /> {busy ? t("feedback.opening") : t("about.sendFeedback")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("feedback.footer", { email: FEEDBACK_EMAIL })}</p>
    </div>
  );
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}
